import { useMemo, useRef, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError, createApiClient } from './api/client';
import { ApiContext } from './api/hooks';
import { useAuth } from './auth/AuthProvider';
import { AuthCallback, RequireAuth } from './auth/screens';
import { ChannelSetup } from './screens/ChannelSetup';
import { HistoryScreen } from './screens/HistoryScreen';
import { LearningScreen } from './screens/learning/LearningScreen';
import { NewReel } from './screens/NewReel';
import { PostScreen } from './screens/PostScreen';
import { ReelList } from './screens/ReelList';
import { RecordScreen } from './screens/record/RecordScreen';
import { ScriptScreen } from './screens/script/ScriptScreen';
import { StatsScreen } from './screens/StatsScreen';
import { VideoScreen } from './screens/VideoScreen';
import { IconSprite } from './ui/Icon';
import { MockFlagContext } from './ui/mockFlag';
import { Screen } from './ui/Screen';
import { ToastProvider } from './ui/Toast';

/** Opt in to React Router v7 behaviour now (silences the upgrade warnings). */
export const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true } as const;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}

/** Builds the API client from the auth session; 401 answers end the session. */
export function ApiFromAuth({ baseUrl, children }: { baseUrl: string; children: ReactNode }) {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const client = useMemo(
    () =>
      createApiClient({
        baseUrl,
        getToken: () => authRef.current.getAccessToken(),
        onUnauthorized: () => authRef.current.expire(),
      }),
    [baseUrl],
  );
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

function NotFound() {
  return (
    <Screen title="Not found" back="/">
      <p className="muted">This page does not exist.</p>
    </Screen>
  );
}

function Guarded() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route element={<Guarded />}>
        <Route path="/" element={<ReelList />} />
        <Route path="/channel" element={<ChannelSetup />} />
        <Route path="/new" element={<NewReel />} />
        <Route path="/learning" element={<LearningScreen />} />
        <Route path="/reels/:reelId" element={<Navigate to="script" replace />} />
        <Route path="/reels/:reelId/script" element={<ScriptScreen />} />
        <Route path="/reels/:reelId/record" element={<RecordScreen />} />
        <Route path="/reels/:reelId/video" element={<VideoScreen />} />
        <Route path="/reels/:reelId/post" element={<PostScreen />} />
        <Route path="/reels/:reelId/stats" element={<StatsScreen />} />
        <Route path="/reels/:reelId/history" element={<HistoryScreen />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** Everything below the auth provider. Tests mount this with a MemoryRouter. */
export function AppShell({
  baseUrl,
  queryClient,
  mock,
  children,
}: {
  baseUrl: string;
  queryClient: QueryClient;
  mock: boolean;
  children: ReactNode;
}) {
  return (
    <MockFlagContext.Provider value={mock}>
      <ApiFromAuth baseUrl={baseUrl}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <IconSprite />
            {children}
          </ToastProvider>
        </QueryClientProvider>
      </ApiFromAuth>
    </MockFlagContext.Provider>
  );
}

export function App({ baseUrl, mock }: { baseUrl: string; mock: boolean }) {
  const queryClient = useMemo(createQueryClient, []);
  return (
    <AppShell baseUrl={baseUrl} queryClient={queryClient} mock={mock}>
      <BrowserRouter future={ROUTER_FUTURE}>
        <AppRoutes />
      </BrowserRouter>
    </AppShell>
  );
}
